import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge, Modal, Form, Row, Col, Table, Spinner, Tabs, Tab, Form as BootstrapForm } from 'react-bootstrap';
import { Wallet2, CheckCircle, XCircle, Eye, FileText, Clock, ArrowLeft, Search, Filter } from 'react-bootstrap-icons';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';

const WalletManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending');
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [statement, setStatement] = useState([]);
  const [transferReport, setTransferReport] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [amountFilter, setAmountFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Function to handle column sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
    setLoading(true);
      
      // Fetch payment requests for different tabs
      const [pendingRes, approvedRes, rejectedRes, statementRes, transferRes] = await Promise.all([
        api.get('/api/test/payment-requests?status=pending'),
        api.get('/api/test/payment-requests?status=approved'),
        api.get('/api/test/payment-requests?status=rejected'),
        api.get('/api/test/payment-requests?status=all'),
        api.get('/api/test/payment-requests?status=all') // Using same endpoint for transfer report
      ]);

      console.log('API Responses:', {
        pending: pendingRes.data,
        approved: approvedRes.data,
        rejected: rejectedRes.data,
        statement: statementRes.data,
        transfer: transferRes.data
      });

      setPending(pendingRes.data.data?.data || []);
      setApproved(approvedRes.data.data?.data || []);
      const rejectedData = rejectedRes.data.data?.data || [];
      console.log('Setting rejected data:', rejectedData.length, rejectedData.map(r => ({ id: r.id, name: r.user?.name, amount: r.amount })));
      setRejected(rejectedData);
      setStatement(statementRes.data.data?.data || []);
      setTransferReport(transferRes.data.data?.data || []);
      
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      // Set empty arrays on error
      setPending([]);
      setApproved([]);
      setRejected([]);
      setStatement([]);
      setTransferReport([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and search functions
  const filterData = (data) => {
    let filtered = [...data];
    
    console.log('Filtering with:', { searchTerm, amountFilter, dateFilter, sortBy, sortOrder });

    // Search filter
    if (searchTerm) {
      const beforeSearch = filtered.length;
      filtered = filtered.filter(item => 
        item.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.utr_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      console.log('After search filter:', beforeSearch, '->', filtered.length);
    }

    // Amount filter
    if (amountFilter !== 'all') {
      const beforeAmount = filtered.length;
      filtered = filtered.filter(item => {
        const amount = item.amount || 0;
        switch (amountFilter) {
          case '0-1000': return amount >= 0 && amount <= 1000;
          case '1000-5000': return amount > 1000 && amount <= 5000;
          case '5000-10000': return amount > 5000 && amount <= 10000;
          case '10000+': return amount > 10000;
          default: return true;
        }
      });
      console.log('After amount filter:', beforeAmount, '->', filtered.length, 'filter:', amountFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const beforeDate = filtered.length;
      const now = new Date();
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.created_at);
        switch (dateFilter) {
          case 'today': return itemDate.toDateString() === now.toDateString();
          case 'week': return (now - itemDate) <= 7 * 24 * 60 * 60 * 1000;
          case 'month': return (now - itemDate) <= 30 * 24 * 60 * 60 * 1000;
          default: return true;
        }
      });
      console.log('After date filter:', beforeDate, '->', filtered.length, 'filter:', dateFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy === 'amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortBy === 'created_at' || sortBy === 'reviewed_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  };

  const getFilteredData = () => {
    let result;
    switch (activeTab) {
      case 'pending': result = filterData(pending); break;
      case 'approved': result = filterData(approved); break;
      case 'rejected': 
        console.log('Rejected data before filtering:', rejected.length, rejected.map(r => ({ id: r.id, name: r.user?.name, amount: r.amount })));
        result = filterData(rejected);
        console.log('Rejected data after filtering:', result.length, result.map(r => ({ id: r.id, name: r.user?.name, amount: r.amount })));
        break;
      case 'statement': result = filterData(statement); break;
      case 'transferReport': result = filterData(transferReport); break;
      default: result = [];
    }
    return result;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setAmountFilter('all');
    setDateFilter('all');
    setSortBy('created_at');
    setSortOrder('desc');
  };

  // Payment request action handlers
  const handleApprove = async (requestId) => {
    try {
      console.log('Approving payment request:', requestId);
      const response = await api.post(`/api/admin/payment-requests/${requestId}/action`, {
        action: 'approve',
        review_note: 'Approved by admin'
      });
      console.log('Approve response:', response.data);
      alert('Payment request approved successfully!');
      // Refresh data
      fetchData();
    } catch (error) {
      console.error('Error approving payment request:', error);
      alert('Failed to approve payment request: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleReject = async (requestId) => {
    const reviewNote = prompt('Please enter a reason for rejection:');
    if (!reviewNote) {
      alert('Review note is required for rejection.');
      return;
    }
    
    try {
      console.log('Rejecting payment request:', requestId);
      const response = await api.post(`/api/admin/payment-requests/${requestId}/action`, {
        action: 'reject',
        review_note: reviewNote
      });
      console.log('Reject response:', response.data);
      alert('Payment request rejected successfully!');
      // Refresh data
      fetchData();
    } catch (error) {
      console.error('Error rejecting payment request:', error);
      alert('Failed to reject payment request: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleView = (request) => {
    // Show request details in a modal or alert
    const details = `
Payment Request Details:
- ID: ${request.id}
- User: ${request.user?.name || 'N/A'} (${request.user?.email || 'N/A'})
- Amount: ₹${request.amount?.toLocaleString() || '0'}
- UTR Number: ${request.utr_number || 'N/A'}
- Description: ${request.description || 'N/A'}
- Transfer Date: ${request.transfer_at ? new Date(request.transfer_at).toLocaleDateString() : 'N/A'}
- Status: ${request.status || 'N/A'}
- Created: ${request.created_at ? new Date(request.created_at).toLocaleDateString() : 'N/A'}
- Review Note: ${request.review_note || 'N/A'}
    `;
    alert(details);
  };

  // Loading component
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          .wallet-management input::placeholder {
            color: rgba(0, 0, 0, 0.6) !important;
          }
          .wallet-management input:focus {
            background-color: rgba(0, 0, 0, 0.08) !important;
            border-color: rgba(0, 0, 0, 0.4) !important;
            color: black !important;
          }
          .wallet-management select option {
            background-color: white !important;
            color: black !important;
          }
          .wallet-management select:focus {
            background-color: rgba(0, 0, 0, 0.08) !important;
            border-color: rgba(0, 0, 0, 0.4) !important;
            color: black !important;
          }
          .wallet-management th[style*="cursor: pointer"]:hover {
            background-color: rgba(0, 0, 0, 0.15) !important;
            transition: background-color 0.2s ease;
          }
        `}
      </style>
      <div className="wallet-management" style={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #dee2e6 100%)', 
        minHeight: '100vh', 
        padding: '2rem 0' 
      }}>
      <div className="container-fluid">
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-lg border-0" style={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              backdropFilter: 'blur(10px)', 
              borderRadius: '20px',
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <Button 
                      variant="outline-dark" 
                      className="me-4 d-flex align-items-center rounded-pill px-3"
                      onClick={() => navigate('/dashboard')}
                      style={{ 
                        borderWidth: '2px',
                        borderColor: 'rgba(0, 0, 0, 0.3)',
                        color: 'black'
                      }}
                    >
                      <ArrowLeft className="me-2" size={16} />
                      Back to Dashboard
                    </Button>
                    <div className="bg-primary rounded-circle p-3 me-3" style={{ width: '60px', height: '60px' }}>
                      <Wallet2 className="text-dark" style={{ fontSize: '1.5rem' }} />
                    </div>
                    <div>
                      <h2 className="mb-1 fw-bold text-dark">
                        Wallet Management
                      </h2>
                      <p className="text-muted mb-0 fs-6">
                        Manage payment requests and wallet transactions • Real-time updates
                      </p>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <span className="badge bg-primary fs-6 px-3 py-2 rounded-pill fw-bold d-flex align-items-center">
                      <Clock className="me-2" size={18} />
                      Real-time Updates
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-lg" style={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}>
              <div className="card-body p-4">
                <div className="row align-items-end">
                  <div className="col-lg-3 col-md-6 mb-3">
                    <label className="form-label fw-bold text-dark mb-2">
                      <Search className="me-2" size={16} />
                      Search
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg rounded-pill"
                      placeholder="Search by name, email, UTR, or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ 
                        border: '2px solid rgba(0, 0, 0, 0.2)', 
                        borderRadius: '25px',
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        color: 'black'
                      }}
                    />
                  </div>
                  
                  <div className="col-lg-2 col-md-6 mb-3">
                    <label className="form-label fw-bold text-dark mb-2">
                      <Filter className="me-2" size={16} />
                      Amount Range
                    </label>
                    <select
                      className="form-select form-select-lg rounded-pill"
                      value={amountFilter}
                      onChange={(e) => setAmountFilter(e.target.value)}
                      style={{ 
                        border: '2px solid rgba(0, 0, 0, 0.2)', 
                        borderRadius: '25px',
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        color: 'black'
                      }}
                    >
                      <option value="all" style={{ backgroundColor: 'white', color: 'black' }}>All Amounts</option>
                      <option value="0-1000" style={{ backgroundColor: 'white', color: 'black' }}>₹0 - ₹1,000</option>
                      <option value="1000-5000" style={{ backgroundColor: 'white', color: 'black' }}>₹1,000 - ₹5,000</option>
                      <option value="5000-10000" style={{ backgroundColor: 'white', color: 'black' }}>₹5,000 - ₹10,000</option>
                      <option value="10000+" style={{ backgroundColor: 'white', color: 'black' }}>₹10,000+</option>
                    </select>
                  </div>

                  <div className="col-lg-2 col-md-6 mb-3">
                    <label className="form-label fw-bold text-dark mb-2">Date Range</label>
                    <select
                      className="form-select form-select-lg rounded-pill"
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      style={{ 
                        border: '2px solid rgba(0, 0, 0, 0.2)', 
                        borderRadius: '25px',
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        color: 'black'
                      }}
                    >
                      <option value="all" style={{ backgroundColor: 'white', color: 'black' }}>All Dates</option>
                      <option value="today" style={{ backgroundColor: 'white', color: 'black' }}>Today</option>
                      <option value="week" style={{ backgroundColor: 'white', color: 'black' }}>This Week</option>
                      <option value="month" style={{ backgroundColor: 'white', color: 'black' }}>This Month</option>
                    </select>
                  </div>

                  <div className="col-lg-2 col-md-6 mb-3">
                    <label className="form-label fw-bold text-dark mb-2">Sort By</label>
                    <select
                      className="form-select form-select-lg rounded-pill"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      style={{ 
                        border: '2px solid rgba(0, 0, 0, 0.2)', 
                        borderRadius: '25px',
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        color: 'black'
                      }}
                    >
                      <option value="created_at" style={{ backgroundColor: 'white', color: 'black' }}>Date Created</option>
                      <option value="amount" style={{ backgroundColor: 'white', color: 'black' }}>Amount</option>
                      <option value="user.name" style={{ backgroundColor: 'white', color: 'black' }}>User Name</option>
                      <option value="utr_number" style={{ backgroundColor: 'white', color: 'black' }}>UTR Number</option>
                      <option value="reviewed_at" style={{ backgroundColor: 'white', color: 'black' }}>Review Date</option>
                    </select>
                  </div>

                  <div className="col-lg-2 col-md-6 mb-3">
                    <label className="form-label fw-bold text-dark mb-2">Order</label>
                    <select
                      className="form-select form-select-lg rounded-pill"
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value)}
                      style={{ 
                        border: '2px solid rgba(0, 0, 0, 0.2)', 
                        borderRadius: '25px',
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        color: 'black'
                      }}
                    >
                      <option value="desc" style={{ backgroundColor: 'white', color: 'black' }}>Descending</option>
                      <option value="asc" style={{ backgroundColor: 'white', color: 'black' }}>Ascending</option>
                    </select>
                  </div>

                  <div className="col-lg-1 col-md-6 mb-3">
                    <Button
                      variant="outline-light"
                      className="w-100 rounded-pill"
                      onClick={clearFilters}
                      style={{ 
                        border: '2px solid rgba(255, 255, 255, 0.3)', 
                        borderRadius: '25px',
                        color: 'black'
                      }}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-lg" style={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}>
              <div className="card-body p-4">
                <Tabs 
                  activeKey={activeTab} 
                  onSelect={(k) => setActiveTab(k)} 
                  className="mb-0"
                  variant="pills"
                  fill
                >
                  <Tab eventKey="pending" title={
                    <span className="d-flex align-items-center fw-bold">
                      <div className="bg-warning rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px' }}>
                        <Clock className="text-dark" size={18} />
                      </div>
                      Pending
                      {pending.length > 0 && (
                        <Badge bg="warning" className="ms-2 fw-bold">{pending.length}</Badge>
                      )}
                    </span>
                  }>
                    <div className="card border-0 shadow-lg mt-3" style={{ 
                      background: 'rgba(255, 255, 255, 0.95)', 
                      backdropFilter: 'blur(10px)',
                      borderRadius: '20px',
                      border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>
                      <div className="card-header border-0" style={{ 
                        background: 'linear-gradient(135deg, #ffc107 0%, #ff8f00 100%)',
                        borderRadius: '20px 20px 0 0'
                      }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <div className="bg-white bg-opacity-20 rounded-circle p-2 me-3">
                              <Clock className="text-dark" size={24} />
                            </div>
                            <div>
                              <h5 className="mb-0 text-dark fw-bold">Pending Requests</h5>
                              <small className="text-dark opacity-75">Awaiting admin approval</small>
                            </div>
                          </div>
                          <span className="badge bg-white text-warning fs-6 px-3 py-2 rounded-pill fw-bold">
                            {getFilteredData().length} Requests
                          </span>
                        </div>
                      </div>
                      <div className="card-body p-4">
                        {getFilteredData().length > 0 ? (
                          <div className="table-responsive">
                            <Table hover className="mb-0">
                              <thead style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}>
                                <tr>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('user.name')}
                                  >
                                    User {sortBy === 'user.name' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('amount')}
                                  >
                                    Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('utr_number')}
                                  >
                                    UTR Number {sortBy === 'utr_number' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('transfer_at')}
                                  >
                                    Transfer Date {sortBy === 'transfer_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('description')}
                                  >
                                    Description {sortBy === 'description' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('created_at')}
                                  >
                                    Requested {sortBy === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th className="fw-bold text-dark">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                                {getFilteredData().map((request) => (
                                  <tr key={request.id}>
                                    <td>
                                      <div>
                                        <div className="fw-bold text-dark">{request.user?.name || 'N/A'}</div>
                                        <small className="text-muted" style={{ opacity: 0.8 }}>{request.user?.email || 'N/A'}</small>
                                      </div>
                                    </td>
                                    <td className="fw-bold text-success">₹{request.amount?.toLocaleString() || '0'}</td>
                                    <td className="font-monospace text-dark">{request.utr_number || 'N/A'}</td>
                                    <td className="text-dark">{request.transfer_at ? new Date(request.transfer_at).toLocaleDateString() : 'N/A'}</td>
                                    <td className="text-dark">{request.description || 'N/A'}</td>
                                    <td className="text-dark">{request.created_at ? new Date(request.created_at).toLocaleDateString() : 'N/A'}</td>
                                    <td>
                                      <div className="d-flex gap-1">
                                        <Button 
                                          variant="success" 
                                          size="sm" 
                                          className=""
                                          onClick={() => handleApprove(request.id)}
                                        >
                                          Approve
                                        </Button>
                                        <Button 
                                          variant="danger" 
                                          size="sm" 
                                          className=""
                                          onClick={() => handleReject(request.id)}
                                        >
                                          Reject
                                        </Button>
                                        <Button 
                                          variant="info" 
                                          size="sm" 
                                          className=""
                                          onClick={() => handleView(request)}
                                        >
                                          View
                                        </Button>
                                      </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                          </div>
                        ) : (
                          <div className="text-center py-5">
                            <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '80px', height: '80px'}}>
                              <Clock size={40} className="text-warning" />
                            </div>
                            <h5 className="text-dark mb-2 fw-bold">No Pending Requests</h5>
                            <p className="text-muted mb-0 fs-6">All payment requests have been processed</p>
                </div>
              )}
                      </div>
                </div>
        </Tab>

                  <Tab eventKey="approved" title={
                    <span className="d-flex align-items-center fw-bold">
                      <div className="bg-success rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px' }}>
                        <CheckCircle className="text-dark" size={18} />
                </div>
                      Approved
                      {approved.length > 0 && (
                        <Badge bg="success" className="ms-2 fw-bold">{approved.length}</Badge>
                      )}
                    </span>
                  }>
                    <div className="card border-0 shadow-lg mt-3" style={{ 
                      background: 'rgba(255, 255, 255, 0.95)', 
                      backdropFilter: 'blur(10px)',
                      borderRadius: '20px',
                      border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>
                      <div className="card-header border-0" style={{ 
                        background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                        borderRadius: '20px 20px 0 0'
                      }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <div className="bg-white bg-opacity-20 rounded-circle p-2 me-3">
                              <CheckCircle className="text-dark" size={24} />
                </div>
                            <div>
                              <h5 className="mb-0 text-dark fw-bold">Approved Requests</h5>
                              <small className="text-dark opacity-75">Successfully approved payments</small>
                </div>
                </div>
                          <span className="badge bg-white text-success fs-6 px-3 py-2 rounded-pill fw-bold">
                            {getFilteredData().length} Requests
                          </span>
                </div>
                </div>
                      <div className="card-body p-4">
                        {getFilteredData().length > 0 ? (
                          <div className="table-responsive">
                            <Table hover className="mb-0">
                              <thead style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}>
                                <tr>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('user.name')}
                                  >
                                    User {sortBy === 'user.name' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('amount')}
                                  >
                                    Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('utr_number')}
                                  >
                                    UTR Number {sortBy === 'utr_number' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('transfer_at')}
                                  >
                                    Transfer Date {sortBy === 'transfer_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('description')}
                                  >
                                    Description {sortBy === 'description' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('reviewed_at')}
                                  >
                                    Approved On {sortBy === 'reviewed_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th className="fw-bold text-dark">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                                {getFilteredData().map((request) => (
                                  <tr key={request.id}>
                                    <td>
                                      <div>
                                        <div className="fw-bold text-dark">{request.user?.name || 'N/A'}</div>
                                        <small className="text-muted" style={{ opacity: 0.8 }}>{request.user?.email || 'N/A'}</small>
                                      </div>
                                    </td>
                                    <td className="fw-bold text-success">₹{request.amount?.toLocaleString() || '0'}</td>
                                    <td className="font-monospace">{request.utr_number || 'N/A'}</td>
                                    <td>{request.transfer_at ? new Date(request.transfer_at).toLocaleDateString() : 'N/A'}</td>
                                    <td>{request.description || 'N/A'}</td>
                                    <td className="text-dark">{request.reviewed_at ? new Date(request.reviewed_at).toLocaleDateString() : 'N/A'}</td>
                                    <td>
                                      <div className="d-flex gap-1">
                                        <Button 
                                          variant="info" 
                                          size="sm" 
                                          className=""
                                          onClick={() => handleView(request)}
                                        >
                                          View
                                        </Button>
                                      </div>
                                    </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                          </div>
                        ) : (
                          <div className="text-center py-5">
                            <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '80px', height: '80px'}}>
                              <CheckCircle size={40} className="text-success" />
                            </div>
                            <h5 className="text-dark mb-2 fw-bold">No Approved Requests</h5>
                            <p className="text-muted mb-0 fs-6">No approved payment requests found</p>
                </div>
              )}
                      </div>
                    </div>
        </Tab>

                  <Tab eventKey="rejected" title={
                    <span className="d-flex align-items-center fw-bold">
                      <div className="bg-danger rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px' }}>
                        <XCircle className="text-dark" size={18} />
                      </div>
                      Rejected
                      {rejected.length > 0 && (
                        <Badge bg="danger" className="ms-2 fw-bold">{rejected.length}</Badge>
                      )}
                    </span>
                  }>
                    <div className="card border-0 shadow-lg mt-3" style={{ 
                      background: 'rgba(255, 255, 255, 0.95)', 
                      backdropFilter: 'blur(10px)',
                      borderRadius: '20px',
                      border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>
                      <div className="card-header border-0" style={{ 
                        background: 'linear-gradient(135deg, #dc3545 0%, #fd7e14 100%)',
                        borderRadius: '20px 20px 0 0'
                      }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <div className="bg-white bg-opacity-20 rounded-circle p-2 me-3">
                              <XCircle className="text-dark" size={24} />
                            </div>
            <div>
                              <h5 className="mb-0 text-dark fw-bold">Rejected Requests</h5>
                              <small className="text-dark opacity-75">Declined payment requests</small>
                            </div>
                          </div>
                          <span className="badge bg-white text-danger fs-6 px-3 py-2 rounded-pill fw-bold">
                            {getFilteredData().length} of {rejected.length} Requests
                          </span>
                        </div>
                      </div>
                      <div className="card-body p-4">
                        {getFilteredData().length > 0 ? (
                          <div className="table-responsive">
                            <Table hover className="mb-0">
                              <thead style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}>
                                <tr>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('user.name')}
                                  >
                                    User {sortBy === 'user.name' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('amount')}
                                  >
                                    Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('utr_number')}
                                  >
                                    UTR Number {sortBy === 'utr_number' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('transfer_at')}
                                  >
                                    Transfer Date {sortBy === 'transfer_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('description')}
                                  >
                                    Description {sortBy === 'description' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('reviewed_at')}
                                  >
                                    Rejected On {sortBy === 'reviewed_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('review_note')}
                                  >
                                    Review Note {sortBy === 'review_note' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th className="fw-bold text-dark">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                                {getFilteredData().map((request) => (
                                  <tr key={request.id} className="border-bottom">
                                    <td>
                                      <div>
                                        <div className="fw-bold text-dark">{request.user?.name || 'N/A'}</div>
                                        <small className="text-muted" style={{ opacity: 0.8 }}>{request.user?.email || 'N/A'}</small>
                                      </div>
                                    </td>
                                    <td className="fw-bold text-danger">₹{request.amount?.toLocaleString() || '0'}</td>
                                    <td className="font-monospace text-dark">{request.utr_number || 'N/A'}</td>
                                    <td className="text-dark">{request.transfer_at ? new Date(request.transfer_at).toLocaleDateString() : 'N/A'}</td>
                                    <td className="text-dark">{request.description || 'N/A'}</td>
                                    <td className="text-dark">{request.reviewed_at ? new Date(request.reviewed_at).toLocaleDateString() : 'N/A'}</td>
                                    <td className="text-dark">{request.review_note || 'N/A'}</td>
                                    <td>
                                      <div className="d-flex gap-1">
                                        <Button 
                                          variant="info" 
                                          size="sm" 
                                          className=""
                                          onClick={() => handleView(request)}
                                        >
                                          View
                                        </Button>
                                      </div>
                                    </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                          </div>
                        ) : (
                          <div className="text-center py-5">
                            <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '80px', height: '80px'}}>
                              <XCircle size={40} className="text-danger" />
                            </div>
                            <h5 className="text-dark mb-2 fw-bold">No Rejected Requests</h5>
                            <p className="text-muted mb-0 fs-6">No rejected payment requests found</p>
                </div>
              )}
                      </div>
                    </div>
        </Tab>

                  <Tab eventKey="statement" title={
                    <span className="d-flex align-items-center fw-bold">
                      <div className="bg-info rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px' }}>
                        <FileText className="text-dark" size={18} />
                      </div>
                      Statement
                      {statement.length > 0 && (
                        <Badge bg="info" className="ms-2 fw-bold">{statement.length}</Badge>
                      )}
                    </span>
                  }>
                    <div className="card border-0 shadow-lg mt-3" style={{ 
                      background: 'rgba(255, 255, 255, 0.95)', 
                      backdropFilter: 'blur(10px)',
                      borderRadius: '20px',
                      border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>
                      <div className="card-header border-0" style={{ 
                        background: 'linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%)',
                        borderRadius: '20px 20px 0 0'
                      }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <div className="bg-white bg-opacity-20 rounded-circle p-2 me-3">
                              <FileText className="text-dark" size={24} />
                            </div>
                            <div>
                              <h5 className="mb-0 text-dark fw-bold">Payment Statement</h5>
                              <small className="text-dark opacity-75">Complete transaction history</small>
                            </div>
                          </div>
                          <span className="badge bg-white text-info fs-6 px-3 py-2 rounded-pill fw-bold">
                            {statement.length} Records
                          </span>
                        </div>
                      </div>
                      <div className="card-body p-4">
                        {getFilteredData().length > 0 ? (
                          <div className="table-responsive">
                            <Table hover className="mb-0">
                              <thead style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}>
                                <tr>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('user.name')}
                                  >
                                    User {sortBy === 'user.name' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('amount')}
                                  >
                                    Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('utr_number')}
                                  >
                                    UTR Number {sortBy === 'utr_number' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('transfer_at')}
                                  >
                                    Transfer Date {sortBy === 'transfer_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('description')}
                                  >
                                    Description {sortBy === 'description' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('status')}
                                  >
                                    Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('created_at')}
                                  >
                                    Request Received {sortBy === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('reviewed_at')}
                                  >
                                    Reviewed On {sortBy === 'reviewed_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('review_note')}
                                  >
                                    Review Note {sortBy === 'review_note' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th className="fw-bold text-dark">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                                {getFilteredData().map((request) => (
                                  <tr key={request.id} className="border-bottom">
                                    <td>
                                      <div>
                                        <div className="fw-bold text-dark">{request.user?.name || 'N/A'}</div>
                                        <small className="text-muted" style={{ opacity: 0.8 }}>{request.user?.email || 'N/A'}</small>
                                        <br />
                                        <small className="badge bg-info text-dark">{request.requester_role || 'N/A'}</small>
                                      </div>
                                    </td>
                                    <td className="fw-bold text-success">₹{request.amount?.toLocaleString() || '0'}</td>
                                    <td className="font-monospace text-dark">{request.utr_number || 'N/A'}</td>
                                    <td className="text-dark">{request.transfer_at ? new Date(request.transfer_at).toLocaleDateString() : 'N/A'}</td>
                                    <td className="text-dark">{request.description || 'N/A'}</td>
                                    <td>
                                      <span className={`badge ${
                                        request.status === 'approved' ? 'bg-success' :
                                        request.status === 'rejected' ? 'bg-danger' :
                                        'bg-warning'
                                      }`}>
                                        {request.status?.charAt(0).toUpperCase() + request.status?.slice(1) || 'N/A'}
                                      </span>
                                    </td>
                                    <td className="text-dark">{request.created_at ? new Date(request.created_at).toLocaleDateString() : 'N/A'}</td>
                                    <td className="text-dark">{request.reviewed_at ? new Date(request.reviewed_at).toLocaleDateString() : 'N/A'}</td>
                                    <td className="text-dark">{request.review_note || 'N/A'}</td>
                                    <td>
                                      <div className="d-flex gap-1">
                                        <Button 
                                          variant="info" 
                                          size="sm" 
                                          className=""
                                          onClick={() => handleView(request)}
                                        >
                                          View
                                        </Button>
                                      </div>
                                    </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                          </div>
                        ) : (
                          <div className="text-center py-5">
                            <div className="bg-info bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '80px', height: '80px'}}>
                              <FileText size={40} className="text-info" />
                            </div>
                            <h5 className="text-dark mb-2 fw-bold">No Statement Records</h5>
                            <p className="text-muted mb-0 fs-6">No payment statement records found</p>
                </div>
              )}
                      </div>
                </div>
        </Tab>

                  <Tab eventKey="transferReport" title={
                    <span className="d-flex align-items-center fw-bold">
                      <div className="bg-secondary rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px' }}>
                        <Wallet2 className="text-dark" size={18} />
                </div>
                      Wallet Transfer Report
                      {transferReport.length > 0 && (
                        <Badge bg="secondary" className="ms-2 fw-bold">{transferReport.length}</Badge>
                      )}
                    </span>
                  }>
                    <div className="card border-0 shadow-lg mt-3" style={{ 
                      background: 'rgba(255, 255, 255, 0.95)', 
                      backdropFilter: 'blur(10px)',
                      borderRadius: '20px',
                      border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>
                      <div className="card-header border-0" style={{ 
                        background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                        borderRadius: '20px 20px 0 0'
                      }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <div className="bg-white bg-opacity-20 rounded-circle p-2 me-3">
                              <Wallet2 className="text-dark" size={24} />
                            </div>
                            <div>
                              <h5 className="mb-0 text-dark fw-bold">Wallet Transfer Report</h5>
                              <small className="text-dark opacity-75">Wallet to wallet transfers</small>
                </div>
                </div>
                          <span className="badge bg-white text-secondary fs-6 px-3 py-2 rounded-pill fw-bold">
                            {transferReport.length} Transfers
                          </span>
                </div>
                </div>
                      <div className="card-body p-4">
                        {getFilteredData().length > 0 ? (
                          <div className="table-responsive">
                            <Table hover className="mb-0">
                              <thead style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}>
                                <tr>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('user.name')}
                                  >
                                    From User {sortBy === 'user.name' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('amount')}
                                  >
                                    Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('utr_number')}
                                  >
                                    UTR Number {sortBy === 'utr_number' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('transfer_at')}
                                  >
                                    Transfer Date {sortBy === 'transfer_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('description')}
                                  >
                                    Description {sortBy === 'description' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('status')}
                                  >
                                    Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th 
                                    className="fw-bold text-dark" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('created_at')}
                                  >
                                    Requested {sortBy === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                                  </th>
                                  <th className="fw-bold text-dark">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                                {getFilteredData().map((request) => (
                                  <tr key={request.id} className="border-bottom">
                                    <td>
                                      <div>
                                        <div className="fw-bold text-dark">{request.user?.name || 'N/A'}</div>
                                        <small className="text-muted" style={{ opacity: 0.8 }}>{request.user?.email || 'N/A'}</small>
                                        <br />
                                        <small className="badge bg-info text-dark">{request.requester_role || 'N/A'}</small>
                                      </div>
                                    </td>
                                    <td className="fw-bold text-success">₹{request.amount?.toLocaleString() || '0'}</td>
                                    <td className="font-monospace text-dark">{request.utr_number || 'N/A'}</td>
                                    <td className="text-dark">{request.transfer_at ? new Date(request.transfer_at).toLocaleDateString() : 'N/A'}</td>
                                    <td className="text-dark">{request.description || 'N/A'}</td>
                                    <td>
                                      <span className={`badge ${
                                        request.status === 'approved' ? 'bg-success' :
                                        request.status === 'rejected' ? 'bg-danger' :
                                        'bg-warning'
                                      }`}>
                                        {request.status?.charAt(0).toUpperCase() + request.status?.slice(1) || 'N/A'}
                                      </span>
                                    </td>
                                    <td className="text-dark">{request.created_at ? new Date(request.created_at).toLocaleDateString() : 'N/A'}</td>
                                    <td>
                                      <div className="d-flex gap-1">
                                        <Button 
                                          variant="info" 
                                          size="sm" 
                                          className=""
                                          onClick={() => handleView(request)}
                                        >
                                          View
                                        </Button>
                                      </div>
                                    </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                          </div>
                        ) : (
                          <div className="text-center py-5">
                            <div className="bg-secondary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '80px', height: '80px'}}>
                              <Wallet2 size={40} className="text-secondary" />
                            </div>
                            <h5 className="text-dark mb-2 fw-bold">No Transfer Records</h5>
                            <p className="text-muted mb-0 fs-6">No wallet transfer records found</p>
                </div>
              )}
                      </div>
            </div>
        </Tab>
      </Tabs>
                </div>
                </div>
                </div>
            </div>
              </div>
                </div>
    </>
  );
};

export default WalletManagement; 
